import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { MAX_DASHBOARD_CHARTS } from "../Dashboard/chartLimit";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
    toJS,
} from "mobx";
import { AuthStatus, AuthStore } from "stores/AuthStore";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import DashboardStore from "../Dashboard/DashboardStore";
import { UserObjectTrackingService } from "services/UserObjectTrackingService";
import { filterByPerms } from "../SignalsUtils";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import type {
    ISignalsDashboard,
} from "../SignalsModels";
import {
    SignalsDashboardType,
    SignalsEndpoints,
    StandardHomes,
    StandardDashboardTypes,
} from "../SignalsModels";
import SignalsEndpointCacheStore from "./SignalsEndpointCacheStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import _ from "lodash";
import { SignalsService } from "services/SignalsService";
import { NavigationType } from "react-router";
import ContextStore from "stores/ContextStore";
import HomeDashboardStore from "stores/HomeDashboardStore";
import { ProactiveInsightsService } from "services/ProactiveInsightsService";
import { UserConfigurationService } from "services/UserConfigurationService";
import type { HomeInsight } from "../Dashboard/HomeInsights.models";

// Per-user, per-org preference for which hierarchies to include in the "What's Changed"
// (Proactive Insights) cards. Org-scoped so one org's hierarchy GUIDs don't bleed into another.
const proactiveInsightsHierarchyIdsKey = (orgId: string) =>
    `proactiveInsightsHierarchyIds::${orgId}`;

// Endpoints whose data depends on which classifiers are published in a group. When a model is
// published/unpublished or moved between groups, charts on these endpoints can show stale bars and must
// be invalidated.
const PUBLISHED_MODEL_RESULTS_ENDPOINT_IDS: string[] = [
    SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId,
    SignalsEndpoints.additionalReports.PublishedModelResultsCountId,
    SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId,
];

/**
 * The SignalsPageStore is the main store for the Signals page.
 * It manages which dashboard is currently selected, has delete/duplicate/create functions for dashboards, etc.
 * It also holds the list of all dashboards available to the user and computed values for user, shared, standard, favorited and home dashboards.
 * @ImportantFunctions loadDashboards(), setCurrentDashboard(), deleteDashboard(), duplicateDashboard()
 * @ImportantComputedValues currentDashboard, userDashboards, sharedDashboards, standardDashboards, favoritedDashboards, userHomeDashboard, orgHomeDashboard, defaultHomeDashboard
 * @OtherObservableValues currentDashboardId, allDashboards, favoriteTrackedGroupId
 */
@AcxStore
class SignalsPageStore extends BaseStore {
    // Stores we need
    readonly authStore: AuthStore;
    readonly rootStore: IRootStore;
    readonly homeDashboardStore: HomeDashboardStore;
    readonly messageStore: MessageStore;
    readonly applicationFiltersStore: ApplicationFiltersStore;
    readonly contextStore: ContextStore;

    // Services we need
    readonly signalsService: SignalsService = new SignalsService();
    readonly proactiveInsightsService = new ProactiveInsightsService();
    readonly userConfigurationService = new UserConfigurationService();
    readonly userObjectTrackingService = new UserObjectTrackingService();

    @observable
    currentDashboardId: string | null = null;

    @observable allDashboards: Map<string, DashboardStore> = new Map([]);
    @observable.ref homeInsights: HomeInsight[] = [];
    // Saved per-user selection of hierarchies to include in the insight cards. Empty = show all.
    @observable.ref homeInsightHierarchyIds: string[] = [];

    @observable favoriteTrackedGroupId;

    @observable shouldResetBuilder: boolean = false;

    private favoriteTrackedGroupIdTask: Promise<void | AsyncTaskStatus.Error> | null =
        null;

    private shouldHideDashboard(dashboard: ISignalsDashboard) {
        return (
            dashboard.dashboardType === SignalsDashboardType.ModelResults &&
            dashboard.title ===
                StandardHomes.get(SignalsDashboardType.ModelResults) &&
            dashboard.signalsChartCount === 0
        );
    }

    private applyDashboardPermissions(dashboard: ISignalsDashboard) {
        dashboard.signalsObjects = filterByPerms(dashboard, this.authStore);
        return dashboard;
    }

    @action
    private upsertDashboardStore(
        dashboard: ISignalsDashboard,
        isFavorite: boolean,
    ) {
        const dashboardStore = new DashboardStore(dashboard, this.rootStore);
        dashboardStore.isFavorite = isFavorite;
        this.allDashboards.get(dashboard.id)?.dispose();
        this.allDashboards.set(dashboard.id, dashboardStore);
    }

    public constructor(rootStore: IRootStore) {
        super("Signals Store");
        makeObservable(this);

        // Global stores
        this.authStore = rootStore.getStore(AuthStore);
        this.messageStore = rootStore.getStore(MessageStore);
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        this.contextStore = rootStore.getStore(ContextStore);
        this.homeDashboardStore = this.authStore.homeDashboardStore;
        this.rootStore = rootStore;

        reaction(
            () => [
                this.homeDashboardStore?.userHomeDashboardId ?? "",
                this.homeDashboardStore?.organizationHomeDashboardId ?? "",
            ],
            () => {
                this.setHomeDashboards();
            },
        );

        reaction(
            () => ({
                effectiveOrgId: this.authStore.effectiveOrgId,
                effectiveUserId: this.authStore.effectiveUserId,
                selectedOrgId:
                    this.authStore.orgStore?.selectedOrganization?.id,
                status: this.authStore.status,
            }),
            async (currentScope, previousScope) => {
                if (
                    currentScope.status !== AuthStatus.Initialized ||
                    !previousScope
                ) {
                    return;
                }

                const scopeChanged =
                    currentScope.effectiveOrgId !==
                        previousScope.effectiveOrgId ||
                    currentScope.effectiveUserId !==
                        previousScope.effectiveUserId ||
                    currentScope.selectedOrgId !== previousScope.selectedOrgId;

                if (scopeChanged) {
                    await this.handleSignalsScopeChange();
                }
            },
        );

        this.setFavoriteTrackedGroupId();
        this.loadDashboards();
    }

    private get canLoadSignalsDashboards() {
        return (
            this.authStore.status === AuthStatus.Initialized &&
            this.authStore.canUserView("Signals")
        );
    }

    @action
    private resetSignalsScopeState() {
        this.currentDashboard?.cancelRefresh();
        this.favoriteTrackedGroupId = undefined;
        this.favoriteTrackedGroupIdTask = null;
        this.allDashboards.forEach((dashboard) => dashboard.dispose());
        this.allDashboards.clear();
    }

    private async handleSignalsScopeChange() {
        this.resetSignalsScopeState();
        await this.setFavoriteTrackedGroupId();
        await this.loadDashboards();
    }

    /**
     * Sets whether the filter builder should be reset.
     * @see ManageApplicationFilters.tsx and @see ManageFiltersButton.tsx for usage
     * @param value Boolean indicating whether the builder should be reset
     */
    @action setShouldResetBuilder(value: boolean) {
        this.shouldResetBuilder = value;
    }

    // Pulls the favorite tracked group ID for the user and sets it in the store
    // Used to get favorite dashboards
    @action
    setFavoriteTrackedGroupId() {
        if (!this.canLoadSignalsDashboards) {
            return Promise.resolve();
        }

        if (this.favoriteTrackedGroupId) {
            return Promise.resolve();
        }

        if (this.favoriteTrackedGroupIdTask) {
            return this.favoriteTrackedGroupIdTask;
        }

        this.favoriteTrackedGroupIdTask = this.setupAsyncTask(
            "Load Favorite Tracked Group ID",
            async () => {
                this.favoriteTrackedGroupId = (
                    await this.userObjectTrackingService.getUserTrackedGroupByName(
                        "Favorite",
                    )
                ).id;
            },
        ).finally(() => {
            this.favoriteTrackedGroupIdTask = null;
        });

        return this.favoriteTrackedGroupIdTask;
    }

    /**
     * Adds a dashboard to the user's favorites. Can be done asynchronously (no need to await). Is also optimistic UI update.
     * @param dashboardId The ID of the dashboard to add to favorites.
     * @returns A promise that resolves when the dashboard is added to favorites.
     */
    @action
    addFavorite = async (dashboardId: string) => {
        return this.setupAsyncTask("AddFavoriteDashboard", async () => {
            try {
                runInAction(() => {
                    if (this.allDashboards.has(dashboardId)) {
                        const dashboard = this.allDashboards.get(dashboardId);
                        if (dashboard) {
                            dashboard.isFavorite = true;
                        }
                    }
                });
                await this.userObjectTrackingService.trackObjectForUser({
                    objectId: dashboardId,
                    objectType: "dashboard",
                    userTrackedGroupId: this.favoriteTrackedGroupId,
                });
            } catch (e) {
                console.error("Error adding favorite dashboard", e);
                runInAction(() => {
                    if (this.allDashboards.has(dashboardId)) {
                        const dashboard = this.allDashboards.get(dashboardId);
                        if (dashboard) {
                            dashboard.isFavorite = false;
                        }
                    }
                });
            }
        });
    };

    /**
     * Removes a dashboard from the user's favorites. Can be done asynchronously (no need to await). Is also optimistic UI update.
     * @param dashboardId The ID of the dashboard to remove from favorites.
     * @returns A promise that resolves when the dashboard is removed from favorites.
     */
    @action
    removeFavorite = async (dashboardId: string) => {
        return this.setupAsyncTask("RemoveFavoriteDashboard", async () => {
            try {
                runInAction(() => {
                    if (this.allDashboards.has(dashboardId)) {
                        const dashboard = this.allDashboards.get(dashboardId);
                        if (dashboard) {
                            dashboard.isFavorite = false;
                        }
                    }
                });
                await this.userObjectTrackingService.unTrackObjectForUser({
                    objectId: dashboardId,
                    objectType: "dashboard",
                    userTrackedGroupId: this.favoriteTrackedGroupId,
                });
            } catch (e) {
                console.error("Error removing favorite dashboard", e);
                runInAction(() => {
                    if (this.allDashboards.has(dashboardId)) {
                        const dashboard = this.allDashboards.get(dashboardId);
                        if (dashboard) {
                            dashboard.isFavorite = true;
                        }
                    }
                });
            }
        });
    };

    @action
    loadHomeInsights = async () => {
        return this.setupAsyncTask("LoadHomeInsights", async () => {
            // Load the saved hierarchy selection first so the initial fetch is already scoped to it.
            await this.loadHomeInsightHierarchyIds();
            await this.fetchHomeInsights();
        });
    };

    private fetchHomeInsights = async () => {
        try {
            const insights = await this.proactiveInsightsService.getLatest(
                this.homeInsightHierarchyIds,
            );

            runInAction(() => {
                this.homeInsights = insights;
            });
        } catch (e) {
            console.error("Error loading home insights", e);
            this.messageStore.logError("Failed to load home insights");
        }
    };

    private loadHomeInsightHierarchyIds = async () => {
        const orgId = this.authStore.orgStore?.selectedOrganization?.id;
        if (!orgId) {
            return;
        }

        try {
            const key = proactiveInsightsHierarchyIdsKey(orgId);
            const configurations =
                await this.userConfigurationService.getUserConfiguration(key);
            const storedValue = configurations.find(
                (config) => config.key === key,
            )?.value;

            let hierarchyIds: string[] = [];
            if (storedValue) {
                const parsed = JSON.parse(storedValue);
                if (Array.isArray(parsed)) {
                    hierarchyIds = parsed.filter(
                        (id): id is string => typeof id === "string",
                    );
                }
            }

            runInAction(() => {
                this.homeInsightHierarchyIds = hierarchyIds;
            });
        } catch (e) {
            console.error("Error loading home insight hierarchy filter", e);
        }
    };

    @action
    saveHomeInsightHierarchyIds = async (hierarchyIds: string[]) => {
        const orgId = this.authStore.orgStore?.selectedOrganization?.id;
        this.homeInsightHierarchyIds = hierarchyIds;

        if (orgId) {
            try {
                await this.userConfigurationService.updateUserConfiguration(
                    proactiveInsightsHierarchyIdsKey(orgId),
                    JSON.stringify(hierarchyIds),
                );
            } catch (e) {
                console.error("Error saving home insight hierarchy filter", e);
                this.messageStore.logError(
                    "Failed to save your insight hierarchy selection",
                );
            }
        }

        // Refetch so the cards reflect the top-ranked insights within the new selection,
        // not just a client-side subset of the previous payload. Reuses the LoadHomeInsights
        // task key so the existing loading skeleton shows during the refetch.
        await this.setupAsyncTask("LoadHomeInsights", () =>
            this.fetchHomeInsights(),
        );
    };

    /**
     * Loads the user's dashboards and sets up the necessary observables. Also sets up the DashboardStores for each dashboard.
     * This is the primary dashboard loading logic for Signals. Changes to dashboard loading likely are here.
     * Checking if the task is done should be done via (thisStore).getTaskLoading("LoadDashboards")
     * @returns A promise that resolves when the dashboards are loaded.
     */
    @action
    loadDashboards = async () => {
        if (!this.canLoadSignalsDashboards) {
            this.resetSignalsScopeState();
            return;
        }

        return this.setupAsyncTask("LoadDashboards", async () => {
            try {
                const dashboards = (
                    await this.signalsService.getDashboards()
                ).filter((dashboard) => !this.shouldHideDashboard(dashboard));
                // We need to filter out the model results main dashboard IFF it has no signals charts (it's empty).
                // In the future this dashboard won't exist but we need this to account for current places where it should be deleted and not just hidden

                dashboards.forEach((dash) => {
                    this.applyDashboardPermissions(dash);
                });

                const standardDashboards = dashboards.filter((dash) =>
                    StandardDashboardTypes.includes(dash.dashboardType),
                );
                const userCustomDashboards = dashboards.filter(
                    (dash) =>
                        !StandardDashboardTypes.includes(dash.dashboardType),
                );

                // Get favorited dashboards
                let favoritedDashboardIds: string[] = [];
                try {
                    if (!this.favoriteTrackedGroupId) {
                        await this.setFavoriteTrackedGroupId();
                    }

                    const dashboardIds =
                        userCustomDashboards?.map(
                            (dashboard) => dashboard.id,
                        ) ?? [];
                    if (
                        !(
                            dashboardIds.length === 0 ||
                            !this.favoriteTrackedGroupId
                        )
                    ) {
                        // Wait for favorites to be pulled
                        const res =
                            await this.userObjectTrackingService.getObjectTrackedStatus(
                                {
                                    objectIds: dashboardIds,
                                    userTrackedGroupId:
                                        this.favoriteTrackedGroupId,
                                },
                            );
                        // Response is a map of dashboardId -> isTracked boolean which corresponds to favorited in our case
                        favoritedDashboardIds = Object.keys(res).filter(
                            (dashboardId) => res[dashboardId] === true,
                        );
                    }
                } catch (err: any) {
                    console.error("Failed to load dashboard favorites", err);
                    if (err) {
                        this.messageStore.logError(
                            "Failed to load dashboard favorites",
                        );
                    }
                }
                // Empty all dashboards before setting up the new ones to avoid duplicates if this is called after initial load for some reason
                this.allDashboards.forEach((dashboard) => dashboard.dispose());
                this.allDashboards.clear();

                // Set allDashboards map up
                [...userCustomDashboards, ...standardDashboards].forEach(
                    (dash) => {
                        this.upsertDashboardStore(
                            dash,
                            favoritedDashboardIds.indexOf(dash.id) > -1,
                        );
                    },
                );

                // Finally, set the home dashboards (different function since logic is large)
                this.setHomeDashboards();
                this.syncDashboardSelectionState();
            } catch (e) {
                console.error("Error loading dashboards", e);
                console.error(JSON.stringify(e));
                this.messageStore.logError("Failed to load dashboards");
            }
        });
    };

    @action
    reloadDashboardsById = async (ids: string[]) => {
        const dashboardIds = Array.from(new Set(ids.filter(Boolean)));
        if (dashboardIds.length === 0) return;

        try {
            for (const dashboardId of dashboardIds) {
                const existingDashboard = this.allDashboards.get(dashboardId);
                const isFavorite = existingDashboard?.isFavorite ?? false;
                existingDashboard?.cancelRefresh();

                const dashboard = await this.signalsService.getDashboard(
                    dashboardId,
                );

                if (!dashboard || this.shouldHideDashboard(dashboard)) {
                    runInAction(() => {
                        this.allDashboards.get(dashboardId)?.dispose();
                        this.allDashboards.delete(dashboardId);
                    });
                    continue;
                }

                this.applyDashboardPermissions(dashboard);
                runInAction(() => {
                    this.upsertDashboardStore(dashboard, isFavorite);
                });
            }

            runInAction(() => {
                this.setHomeDashboards();
                this.syncDashboardSelectionState();
            });
        } catch (e) {
            console.error("Error reloading dashboards", e);
            this.messageStore.logError("Failed to reload dashboards");
        }
    };

    /**
     * Sets the userHomeDashboard, orgHomeDashboard, and defaultHomeDashboard based on the authStore.homeDashboardStore
     */
    @action setHomeDashboards = () => {
        this.allDashboards.forEach((dashboard) => {
            dashboard.setIsUserHomeDashboard(false);
            dashboard.setIsOrgHomeDashboard(false);
        });

        // Set the userHomeDashboard
        // homeDashboardStore.userHomeDashboardId can be either a dashboard ID (abc-123-def-456 uuid)
        // or a standard dashboard route (e.g. "drivers" or "eddy")
        const userHomeDashboardId =
            this.authStore.homeDashboardStore.userHomeDashboardId;
        if (userHomeDashboardId) {
            if (this.allDashboards.has(userHomeDashboardId)) {
                // A double confirmation that the dashboard exists in our loaded dashboards and is not null for some reason
                const homeDashboard =
                    this.allDashboards.get(userHomeDashboardId);
                if (homeDashboard) {
                    // If so, mark it as the user home dashboard
                    // The computed value userHomeDashboard will pick it up and can be used to refer to this dashboard
                    homeDashboard.setIsUserHomeDashboard(true);
                }
            } else {
                // If the home dashboard id is provided, but its not an id in our map,
                // then the homeDashboardId is probably the route of a standard home dashboard (e.g. "drivers" or "eddy")
                // we want to set the user home dashboard to that instead
                const standardHomeDashboard = this.standardDashboards.find(
                    (dash) =>
                        dash.route === userHomeDashboardId &&
                        dash.isStandardHomeDashboard,
                );
                if (standardHomeDashboard) {
                    standardHomeDashboard.setIsUserHomeDashboard(true);
                }
            }
            // If we got down here and didn't find a dashboard, then the user home dashboard is null
        }

        // Set the orgHomeDashboard
        // homeDashboardStore.organizationHomeDashboardId can be either a dashboard ID or a standard dashboard route
        // Logic is all the same as user home dashboard above, look at those comments for more explanation
        const orgHomeDashboardId =
            this.authStore.homeDashboardStore.organizationHomeDashboardId;
        if (orgHomeDashboardId) {
            if (this.allDashboards.has(orgHomeDashboardId)) {
                const homeDashboard =
                    this.allDashboards.get(orgHomeDashboardId);
                if (homeDashboard) {
                    homeDashboard.setIsOrgHomeDashboard(true);
                }
            } else {
                // If the home dashboard id is provided, but its not an id in our map,
                // then the homeDashboardId is actually the route of a standard home dashboard
                // we want to set the user home dashboard to that instead
                const standardHomeDashboard = this.standardDashboards.find(
                    (dash) =>
                        dash.route === orgHomeDashboardId &&
                        dash.isStandardHomeDashboard,
                );
                if (standardHomeDashboard) {
                    standardHomeDashboard.setIsOrgHomeDashboard(true);
                }
            }
        }
    };

    /**
     * Sets the current dashboard based on the provided route or ID.
     * @param dashboardRoute The route of the dashboard to set. Check SignalsModels.DashboardTypeRoutes for valid routes.
     * @param dashboardId The ID of the dashboard to set.
     */
    @action setCurrentDashboard({
        dashboardRoute,
        dashboardId,
        navigationType,
        navigate,
        locationPathname,
    }: {
        dashboardRoute?: string;
        dashboardId?: string;
        navigationType?: NavigationType;
        navigate?: (path: string) => void;
        locationPathname?: string;
    }) {
        // Cancel any ongoing refresh on the current dashboard
        this.currentDashboard?.cancelRefresh();

        // Do we have a pinned filter on the current dashboard before we change it?
        const previouslyHadPinnedFilter = this.currentDashboard
            ?.pinnedApplicationFilterId
            ? true
            : false;

        if (dashboardId) {
            this.currentDashboardId = dashboardId;
        } else if (dashboardRoute) {
            // If no dashboardId is provided, but a dashboardRoute is, we want to set the current dashboard to the standard home dashboard for that type
            const standardHomeDashboard = this.standardDashboards.find(
                (dash) =>
                    dash.route === dashboardRoute &&
                    dash.isStandardHomeDashboard,
            );
            if (standardHomeDashboard) {
                this.currentDashboardId = standardHomeDashboard.id;
            } else {
                this.currentDashboardId = null;
            }
        } else {
            this.currentDashboardId = null;
        }

        // A tenant can legitimately have no dashboards. In that case we keep Signals mounted,
        // don't try to resolve a default home, and leave the main area empty.
        if (this.allDashboards.size === 0) {
            this.currentDashboardId = null;
            this.syncDashboardSelectionState();
            return;
        }

        // If after setting the current dashboard we find that the dashboard is invalid, redirect to home
        // This will also run if there was never a dashboardId or dashboardRoute to begin with, which means we will redirect to home in that case as well
        if (
            (this.currentDashboardId &&
                !this.allDashboards.has(this.currentDashboardId)) ||
            this.currentDashboardId == null
        ) {
            // First check if we're on Discover, if so we want to go to default home dashboard
            if (
                (locationPathname?.includes("discover") ||
                    dashboardRoute === "discover") &&
                this.defaultHomeDashboard
            ) {
                this.currentDashboardId = this.defaultHomeDashboard.id;
                if (locationPathname !== "/app/signals/discover") {
                    navigate?.(`/app/signals/discover`);
                }
            }

            // The dashboard id was invalid, so we're going to the home dashboard
            // Prioritization: user home (reporting) -> user home dashboard -> org home (reporting) ---
            // ---> org home dashboard -> default home dashboard
            else if (
                this.homeDashboardStore.userHomeDashboardId === "reporting"
            ) {
                // If the homeDashboardId is "reporting", we want to go to the reporting home
                // We check this before checking for user/org home dashboards existing
                navigate?.(`/app/signals/reports/home`);
            } else if (this.userHomeDashboard) {
                this.currentDashboardId = this.userHomeDashboard.id;

                // We navigate the user to the home dashboard route
                // assuming they aren't already there
                if (locationPathname !== "/app/signals/home") {
                    navigate?.(`/app/signals/home`);
                }
            } else if (
                this.homeDashboardStore.organizationHomeDashboardId ===
                "reporting"
            ) {
                // Check for the org home being reports before trying to use org home dashboard
                navigate?.(`/app/signals/reports/home`);
            } else if (this.orgHomeDashboard) {
                this.currentDashboardId = this.orgHomeDashboard.id;
                // Replace url to home dashboard
                // Only do this if its not already set to that for some reason
                // This mainly lets us have /app/signals also go to home dashboard
                if (locationPathname !== "/app/signals/home") {
                    navigate?.(`/app/signals/home`);
                }
            } else if (this.defaultHomeDashboard) {
                // We have no home dashboard set whatsoever, so we're going to the default home
                // Default home is set in SignalsPageStore.ts
                this.currentDashboardId = this.defaultHomeDashboard.id;
                if (locationPathname !== "/app/signals/home") {
                    navigate?.(`/app/signals/home`);
                }
            } else {
                this.currentDashboardId = null;
            }
        }

        this.syncDashboardSelectionState();

        // After setting the new current dashboard, we need to set up the filters for it
        this.dashboardFiltersSetup({
            navigationType,
            previouslyHadPinnedFilter,
        });
    }

    @action
    private syncDashboardSelectionState() {
        this.allDashboards.forEach((dashboard) => {
            dashboard.setIsSelected(dashboard.id === this.currentDashboardId);
        });
    }

    /** Setup the dashboard filters after setting the current dashboard. Separated out for readability */
    @action dashboardFiltersSetup({
        navigationType,
        previouslyHadPinnedFilter,
    }: {
        navigationType?: NavigationType;
        previouslyHadPinnedFilter: boolean;
    }) {
        const beforeDrilldownFilters =
            this.applicationFiltersStore?.savedFilterStates?.[
                "before-drilldown"
            ];
        if (
            navigationType === NavigationType.Pop &&
            beforeDrilldownFilters &&
            !this.currentDashboard?.pinnedApplicationFilterId
        ) {
            this.contextStore.clearBannerContent();

            this.applicationFiltersStore.applyExternalFilters(
                beforeDrilldownFilters,
                true,
            );
        }

        // Does the new current dashboard have a pinned filter?
        const newDashboardHasPinnedFilter = this.currentDashboard
            ?.pinnedApplicationFilterId
            ? true
            : false;

        if (
            newDashboardHasPinnedFilter &&
            this.currentDashboard &&
            this.currentDashboard.pinnedApplicationFilterId
        ) {
            // If the new dashboard has a pinned filter, we need to apply it
            if (!previouslyHadPinnedFilter) {
                // If we didn't have a pinned filter before, save the current filter state
                // We don't want to save the state if it was a pinned filter already,
                // because that would overwrite the "beforePinnedFilters" state (which should have been set going to THAT dashboard originally)
                this.applicationFiltersStore.saveCurrentFilterState(
                    "beforePinnedFilters",
                );
                this.contextStore.setPrevious(
                    _.cloneDeep(this.applicationFiltersStore.toFilterObject()),
                    this.applicationFiltersStore.selectedSavedFilter,
                );
            }
            // Apply the pinned filter and set it as the selected filter
            this.applicationFiltersStore.applySavedFilters(
                this.currentDashboard.pinnedApplicationFilterId,
            );
            const filters = this.applicationFiltersStore.getSavedFilterById(
                this.currentDashboard.pinnedApplicationFilterId,
            );
            this.applicationFiltersStore.setSelectedSavedFilters(filters);
            this.applicationFiltersStore.saveCurrentFilterState("Update");

            this.applicationFiltersStore.saveCurrentFilterState("Signals");
        } else if (
            previouslyHadPinnedFilter &&
            this.currentDashboard &&
            !newDashboardHasPinnedFilter
        ) {
            // If we came from a dashboard with a pinned filter, and the new one DOESN'T have one,
            // we need to revert to the filters before any pinned filter was applied
            this.restoreFiltersBeforePinnedDashboard();
        }

        // If the filters have changed since we last applied to this dashboard, refresh it
        if (
            this.currentDashboard &&
            (this.currentDashboard.lastAppliedFilters === null ||
                !_.isEqual(
                    toJS(this.currentDashboard.lastAppliedFilters),
                    this.applicationFiltersStore.toFilterObject(),
                ) ||
                this.applicationFiltersStore.hasFilterStateChangedSince(
                    "Signals",
                ))
        ) {
            this.currentDashboard.applyFilters("auto");
        }
    }

    @action
    private restoreFiltersBeforePinnedDashboard() {
        const beforePinnedFilters =
            this.contextStore.previousFilters ??
            this.applicationFiltersStore.savedFilterStates["beforePinnedFilters"];
        this.applicationFiltersStore.resetAllFilters();
        if (beforePinnedFilters) {
            this.applicationFiltersStore.applyExternalFilters(
                beforePinnedFilters,
                true, // Also affect the quick filters
            );
            const previousSelectedSavedFilter =
                this.contextStore.previousSelectedSavedFilter;
            this.applicationFiltersStore.setSelectedSavedFilters(
                this.applicationFiltersStore.getSavedFilterById(
                    previousSelectedSavedFilter?.id,
                ) ?? previousSelectedSavedFilter,
            );
        }
        this.applicationFiltersStore.saveCurrentFilterState("Update");
        this.applicationFiltersStore.saveCurrentFilterState("Signals");
        this.contextStore.setPrevious(undefined, undefined);
        delete this.applicationFiltersStore.savedFilterStates["beforePinnedFilters"];
    }

    /**
     * Clears cached published-model-results chart DATA after a model is published/unpublished or moved
     * between groups, so the affected grouped/individual charts refetch fresh bars instead of serving the
     * 5-minute endpoint cache. This is the piece that the model-change flows otherwise miss —
     * targeted dashboard reloads rebuild chart stores and their structure/order rules but do NOT touch this
     * separate endpoint cache, so a rebuilt chart would still hit a stale entry.
     */
    @action
    invalidatePublishedModelResultsCharts() {
        const cacheStore = this.rootStore.getStore(SignalsEndpointCacheStore);
        PUBLISHED_MODEL_RESULTS_ENDPOINT_IDS.forEach((endpointId) =>
            cacheStore.clearEndpointsByBaseId(endpointId),
        );
    }

    createDashboard = async ({
        title,
        description,
        logoName,
    }: {
        title: string;
        description?: string;
        logoName?: string;
    }) => {
        try {
            const newDash = await this.signalsService.createDashboard({
                title,
                description,
                logoName,
            });
            const newDashStore = new DashboardStore(newDash, this.rootStore);

            runInAction(() => {
                this.allDashboards.set(newDash.id, newDashStore);
            });

            this.messageStore.logMessage(
                "Dashboard successfully created",
                "success",
            );
            return newDashStore;
        } catch (e) {
            this.messageStore.logMessage("Failed to create dashboard", "error");
            return null;
        }
    };

    /**
     * Deletes a dashboard by ID.
     * @param dashboardId The ID of the dashboard to delete.
     * @returns Whether deletion succeeded. Progress can be tracked via getTaskLoading("DeleteDashboard")
     */
    deleteDashboard = async (dashboardId: string): Promise<boolean> => {
        const result = await this.setupAsyncTask("DeleteDashboard", async () => {
            try {
                await this.signalsService.deleteDashboard({
                    id: dashboardId,
                });
                runInAction(() => {
                    const dashboard = this.allDashboards.get(dashboardId);
                    dashboard?.cancelRefresh();
                    if (
                        this.currentDashboardId === dashboardId &&
                        dashboard?.pinnedApplicationFilterId
                    ) {
                        // Restore before removing the dashboard: navigation can no longer
                        // detect its pinned filter once currentDashboard becomes null.
                        this.restoreFiltersBeforePinnedDashboard();
                    }
                    dashboard?.dispose();
                    this.allDashboards.delete(dashboardId);
                });
                this.messageStore.logMessage(
                    "Dashboard successfully deleted",
                    "success",
                );
                return true;
            } catch (e) {
                this.messageStore.logMessage(
                    "Failed to delete dashboard",
                    "error",
                );
                return false;
            }
        });
        return result === true;
    };

    /**
     * Duplicates a dashboard.
     * @param id The ID of the dashboard to duplicate.
     * @param title The title for the new dashboard.
     * @returns Promise with the new duplicated DashboardStore, or null if duplication failed.
     */
    @action
    duplicateDashboard = async ({ id, title }) => {
        try {
            const newDash = await this.signalsService.duplicateDashboard({
                id,
                title,
            });
            const newDashStore = new DashboardStore(newDash, this.rootStore);
            runInAction(() => {
                newDashStore.signalsObjects = filterByPerms(
                    newDash,
                    this.authStore,
                );

                this.allDashboards.set(newDash.id, newDashStore);
            });
            this.messageStore.logMessage(
                "Dashboard successfully duplicated",
                "success",
            );
            return newDashStore;
        } catch (e) {
            this.messageStore.logMessage(
                "Failed to duplicate dashboard",
                "error",
            );
            return null;
        }
    };

    /**
     * The user's custom dashboards (not standard) that they own (not shared)
     */
    @computed get userDashboards(): DashboardStore[] {
        return Array.from(this.allDashboards.values())
            .filter(
                (dash) =>
                    dash.dashboardType === SignalsDashboardType.Custom &&
                    dash.userVisibility === ReportAccessType.Owner,
            )
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    /**
     * The user's custom dashboards (not standard) that they have access to (shared with them)
     */
    @computed get sharedDashboards(): DashboardStore[] {
        return Array.from(this.allDashboards.values())
            .filter(
                (dash) =>
                    dash.dashboardType === SignalsDashboardType.Custom &&
                    dash.userVisibility !== ReportAccessType.Owner,
            )
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    /**
     * Dashboards a card can be pinned to: editable, with fewer than 20 charts,
     * by title. Computed so it follows each dashboard's chart count, which
     * changes in place when cards are added or deleted.
     */
    @computed get pinTargetDashboards(): DashboardStore[] {
        return this.userDashboards
            .concat(this.sharedDashboards)
            .filter(
                (dash) =>
                    dash.dashboardIsEditable &&
                    dash.signalsChartCount < MAX_DASHBOARD_CHARTS,
            )
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    /** The standard dashboards available to the user */
    @computed get standardDashboards(): DashboardStore[] {
        const standardDashboards = Array.from(
            this.allDashboards.values(),
        ).filter((dash) => StandardDashboardTypes.includes(dash.dashboardType));

        const standardTypeOrder = [
            SignalsDashboardType.ThemesAndDrivers,
            SignalsDashboardType.ThemesAndTopics,
            SignalsDashboardType.Eddy,
            SignalsDashboardType.Trends,
            SignalsDashboardType.ContactTypes,
            SignalsDashboardType.ModelResults,
            SignalsDashboardType.Hidden,
        ];

        const sortWithinType = (a: DashboardStore, b: DashboardStore) => {
            if (a.isStandardHomeDashboard && !b.isStandardHomeDashboard) {
                return -1;
            }
            if (!a.isStandardHomeDashboard && b.isStandardHomeDashboard) {
                return 1;
            }

            return a.title.localeCompare(b.title);
        };

        const orderedByType = standardTypeOrder.flatMap((dashboardType) =>
            standardDashboards
                .filter((dash) => dash.dashboardType === dashboardType)
                .sort(sortWithinType),
        );

        const unknownTypeDashboards = standardDashboards
            .filter((dash) => !standardTypeOrder.includes(dash.dashboardType))
            .sort(sortWithinType);

        return [...orderedByType, ...unknownTypeDashboards];
    }

    /** All dashboards that are favorited by the user.
     * Does contain duplicates from the other categories (user, shared, standard).
     * Favorites will appear in both */
    @computed get favoritedDashboards(): DashboardStore[] {
        return [...this.userDashboards, ...this.sharedDashboards]
            .filter((dash) => dash.isFavorite)
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    /** All dashboards as an array, sorted by title */
    @computed get allDashboardsArray(): DashboardStore[] {
        return Array.from(this.allDashboards.values()).sort((a, b) =>
            a.title.localeCompare(b.title),
        );
    }

    /**
     * The currently selected dashboard based on currentDashboardId
     * May be null if currentDashboardId is invalid (not found in allDashboards)
     */
    @computed get currentDashboard(): DashboardStore | null {
        if (
            this.currentDashboardId &&
            this.allDashboards.has(this.currentDashboardId)
        ) {
            return this.allDashboards.get(this.currentDashboardId) || null;
        }
        return null;
    }

    /** The user's home dashboard, if set */
    @computed get userHomeDashboard(): DashboardStore | null {
        return (
            Array.from(this.allDashboards.values()).find(
                (dash) => dash.isUserHomeDashboard,
            ) ?? null
        );
    }

    /** The organization's home dashboard, if set */
    @computed get orgHomeDashboard(): DashboardStore | null {
        return (
            Array.from(this.allDashboards.values()).find(
                (dash) => dash.isOrgHomeDashboard,
            ) ?? null
        );
    }

    /**
     * The default home dashboard based on user permissions. If the user can view Drivers, the Themes and Drivers dashboard is returned.
     * Otherwise, the Eddy dashboard is returned. If neither is found in the standardDashboards, null is returned. If we want to change
     * the default home dashboard type, we change it here.
     */
    // THIS IS THE TRUE DEFAULT HOME DASHBOARD IF NO USER OR ORG HOME DASHBOARD IS SET
    // IF WE WANT TO CHANGE IT WE CHANGE HERE
    @computed get defaultHomeDashboard(): DashboardStore | null {
        if (this.authStore.canUserView("Drivers")) {
            const themesAndDriversDashboard = this.standardDashboards.find(
                (dash) =>
                    dash.dashboardType ===
                        SignalsDashboardType.ThemesAndDrivers &&
                    dash.isStandardHomeDashboard,
            );

            if (themesAndDriversDashboard) {
                return themesAndDriversDashboard;
            }
        }

        return (
            this.standardDashboards.find(
                (dash) =>
                    dash.dashboardType === SignalsDashboardType.Eddy &&
                    dash.isStandardHomeDashboard,
            ) ?? null
        );
    }
}

export default SignalsPageStore;
